import React, { useState, useEffect } from 'react';
import monitoring from '../services/monitoring';
import { useAnalytics } from '../hooks/monitoring';

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

const MonitoringDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const { trackClick } = useAnalytics();

  useEffect(() => {
    // Update metrics every 5 seconds
    const interval = setInterval(() => {
      const currentMetrics = monitoring.getPerformanceMetrics();
      setMetrics(currentMetrics);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
    trackClick('monitoring_dashboard_toggle', { visible: !isVisible });
  };

  const clearMetrics = () => {
    monitoring.clearPerformanceMetrics();
    setMetrics([]);
    trackClick('monitoring_dashboard_clear');
  };

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'good':
        return '#22c55e';
      case 'needs-improvement':
        return '#f59e0b';
      case 'poor':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const formatValue = (name: string, value: number) => {
    if (name === 'CLS') {
      return value.toFixed(3);
    }
    return `${Math.round(value)}ms`;
  };

  const getMetricDescription = (name: string) => {
    switch (name) {
      case 'CLS':
        return 'Cumulative Layout Shift - Visual stability';
      case 'FID':
        return 'First Input Delay - Interactivity';
      case 'FCP':
        return 'First Contentful Paint - Loading performance';
      case 'LCP':
        return 'Largest Contentful Paint - Loading performance';
      case 'TTFB':
        return 'Time to First Byte - Server response time';
      default:
        return 'Performance metric';
    }
  };

  if (!isVisible) {
    return (
      <button
        onClick={toggleVisibility}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center text-xl"
        style={{
          backgroundColor: 'var(--accent)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = 'var(--accent)';
        }}
        title="Show Monitoring Dashboard"
      >
        📊
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 border rounded-lg shadow-xl"
      style={{
        backgroundColor: 'var(--bg-primary)',
        borderColor: 'var(--border)',
      }}
    >
      <div
        className="flex items-center justify-between p-4 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <h3
          className="text-lg font-semibold"
          style={{ color: 'var(--text-primary)' }}
        >
          Performance Monitoring
        </h3>
        <div className="flex gap-2">
          <button
            onClick={clearMetrics}
            className="px-3 py-1 text-xs rounded-md transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            Clear
          </button>
          <button
            onClick={toggleVisibility}
            className="px-2 py-1 text-xs rounded-md transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {metrics.length === 0 ? (
        <div className="p-4 text-center">
          <p
            className="text-sm mb-2"
            style={{ color: 'var(--text-secondary)' }}
          >
            No performance metrics collected yet.
          </p>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            Navigate around the app to collect Web Vitals data.
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {metrics.map((metric, index) => (
            <div
              key={`${metric.name}-${index}`}
              className="rounded-lg p-3"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className="font-medium text-sm"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {metric.name}
                </span>
                <span
                  className="px-2 py-1 text-xs font-medium text-white rounded-full"
                  style={{ backgroundColor: getRatingColor(metric.rating) }}
                >
                  {metric.rating}
                </span>
              </div>
              <div
                className="text-xl font-bold mb-1"
                style={{ color: 'var(--text-primary)' }}
              >
                {formatValue(metric.name, metric.value)}
              </div>
              <div
                className="text-xs"
                style={{ color: 'var(--text-secondary)' }}
              >
                {getMetricDescription(metric.name)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        className="p-3 border-t rounded-b-lg"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-secondary)',
        }}
      >
        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          🔄 Auto-refreshes every 5 seconds • 💡 Web Vitals are collected
          automatically
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboard;
